import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IPicture, PictureFlag } from "../../models";
import { AppState } from "../main.reducer";
import { getAllPicturesAdminApi } from "./api/getPicturesAdminApi";
import { getAllPicturesApi } from "./api/getPicturesApi";
import { updatePictureIsDeletedApi } from "./api/updatePictureIsDeletedApi";
import { updatePictureOrdersApi } from "./api/updatePictureOrdersApi";
import { updatePictureInfoApi } from "./api/updatePictureInfoApi";
import { updatePicturePricesApi } from "./api/updatePicturePricesApi";
import { updatePictureStateApi } from "./api/updatePictureStateApi";
import { updatePictureFlagApi } from "./api/updatePictureFlagApi";
import { localizePictures } from "./catalog";
import {
  DIFFICULTY_FILTER_CHANGE,
  HIDE_TOAST_FAV_NOTIFICATION,
  LOAD_FAVORITES,
  SELECT_PICTURE,
} from "./pictures.actions";
import { getEquipmentPrice, getEquipmentStatus } from "../../common/utils";
import { Currency } from "../../common/constants";
import { Difficulty } from "../../common/difficulty";

export const usePictures = () => {
  const allPictures = useSelector<AppState, IPicture[]>(
    (store) => store.pictures.allPictures
  );
  const allPicturesAdmin = useSelector<AppState, IPicture[]>(
    (store) => store.pictures.allPicturesAdmin
  );
  const favorites = useSelector<AppState, number[]>(
    (store) => store.pictures.favorites
  );
  const selectedPicture = useSelector<AppState, IPicture | undefined>(
    (store) => store.pictures.selectedPicture
  );
  const availableOnly = useSelector<AppState, boolean>(
    (store) => store.pictures.availableOnly
  );
  const difficultyFilter = useSelector<AppState, Difficulty[]>(
    (store) => store.pictures.difficultyFilter
  );
  const notifyFavorites = useSelector<AppState, boolean>(
    (store) => store.pictures.notifyFavorites
  );

  const currency = useSelector<AppState, Currency>(
    (store) => store.common.currency
  );

  const equipmentPrice = useMemo(() => {
    const prices = getEquipmentPrice(allPictures);
    return {
      ...prices,
      shipping: currency === "KM" ? prices.shippingBih : prices.shipping,
    };
  }, [allPictures, currency]);
  // What the visitor's country sees: hidden pictures left out, status = that country's stock
  const catalogPictures = localizePictures(allPictures, currency);
  const equipmentState = useMemo(
    () => getEquipmentStatus(catalogPictures),
    [catalogPictures]
  );

  const dispatch = useDispatch();

  const getAllPictures = () => getAllPicturesApi(dispatch);
  const getAllPicturesAdmin = () => getAllPicturesAdminApi(dispatch);

  const loadFavorites = (favorites: number[]) =>
    dispatch({ type: LOAD_FAVORITES, favorites });

  const setDifficultyFilter = (filter: Difficulty[]) =>
    dispatch({ type: DIFFICULTY_FILTER_CHANGE, filter });

  const selectPicture = (picture: IPicture | undefined) =>
    dispatch({ type: SELECT_PICTURE, picture });

  const updatePictureState = (pictureId: number, status: boolean) =>
    updatePictureStateApi(dispatch, pictureId, !status);

  const updatePictureIsDeleted = (pictureId: number, isDeleted: boolean) =>
    updatePictureIsDeletedApi(dispatch, pictureId, !isDeleted);

  const updatePictureFlag = (
    pictureId: number,
    flag: PictureFlag,
    value: boolean
  ) => updatePictureFlagApi(dispatch, pictureId, flag, value);

  const updatePictureOrders = (codes: string[]) =>
    updatePictureOrdersApi(dispatch, codes);

  const updatePicturePrices = (
    pictureId: number,
    price: number,
    priceNoDiscount?: number | null
  ): Promise<boolean> =>
    updatePicturePricesApi(dispatch, pictureId, price, priceNoDiscount);

  const updatePictureInfo = (
    pictureId: number,
    title: string,
    sizeX: number,
    sizeY: number,
    category: number,
    difficulty: Difficulty | null
  ): Promise<boolean> =>
    updatePictureInfoApi(
      dispatch,
      pictureId,
      title,
      sizeX,
      sizeY,
      category,
      difficulty
    );

  const hideNotificationFav = () => {
    dispatch({ type: HIDE_TOAST_FAV_NOTIFICATION });
  };

  // React.useEffect(() => {
  // //   if (allPictures.length === 0) getAllPictures();
  // //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // // }, []);

  return {
    allPictures,
    catalogPictures,
    allPicturesAdmin,
    equipmentPrice,
    equipmentState,
    favorites,
    selectedPicture,
    availableOnly,
    difficultyFilter,
    notifyFavorites,
    getAllPictures,
    getAllPicturesAdmin,
    loadFavorites,
    setDifficultyFilter,
    selectPicture,
    updatePictureState,
    updatePictureIsDeleted,
    updatePictureFlag,
    updatePictureOrders,
    hideNotificationFav,
    updatePicturePrices,
    updatePictureInfo,
  };
};
